/*
 * "Zip Attachments" - replaces every file attached to the message being
 * composed with a single, maximally compressed .zip.
 */

/* global Office */

import {
  addBase64Attachment,
  getAttachmentContent,
  getAttachments,
  getComposeItem,
  getSubject,
  notifyError,
  notifyInfo,
  notifyProgress,
  removeAttachment,
} from "../common/mailbox";
import {
  MAX_BASE64_LENGTH,
  ZipInput,
  base64Bytes,
  createZipBase64,
  ensureExtension,
  formatBytes,
  sanitizeFileName,
  shortenForDisplay,
} from "../common/zip";
import { s as t } from "../common/strings";

const KEY = "zipAttachments";

export async function zipAttachments(event: Office.AddinCommands.Event): Promise<void> {
  try {
    const item = getComposeItem();
    if (!item) {
      await notifyError(KEY, t().composeOnlyZip);
      return;
    }

    const attachments = await getAttachments(item);
    // Inline images belong to the body - zipping them would leave broken images.
    const inlineCount = attachments.filter((a) => a.isInline).length;
    const targets = attachments.filter(
      (a) => !a.isInline && a.attachmentType !== Office.MailboxEnums.AttachmentType.Cloud
    );
    const cloudCount = attachments.length - inlineCount - targets.length;

    if (targets.length === 0) {
      await notifyInfo(KEY, t().nothingToZip);
      return;
    }

    const files: ZipInput[] = [];
    let originalBytes = 0;
    for (let i = 0; i < targets.length; i++) {
      const attachment = targets[i];
      await notifyProgress(KEY, t().readingAttachment(i + 1, targets.length));
      const content = await getAttachmentContent(item, attachment.id);
      const entry = toZipInput(attachment.name, content);
      if (entry) {
        files.push(entry);
        originalBytes += attachment.size || 0;
      }
    }

    if (files.length === 0) {
      await notifyError(KEY, t().noAttachmentsReadable);
      return;
    }

    await notifyProgress(KEY, t().compressingCount(files.length));
    // JSZip reports progress very often; the notification bar only needs a nudge.
    let lastShown = 0;
    const base64 = await createZipBase64(files, (percent) => {
      const step = Math.round(percent);
      if (step >= lastShown + 5) {
        lastShown = step;
        notifyProgress(KEY, t().compressingPercent(step));
      }
    });

    if (base64.length > MAX_BASE64_LENGTH) {
      await notifyError(KEY, t().tooLargeToAttach(formatBytes(base64Bytes(base64))));
      return;
    }

    const zipName = await buildZipName(
      item,
      attachments.map((a) => a.name)
    );

    // Attach first, remove the originals only once the zip is safely in place.
    await notifyProgress(KEY, t().attaching(shortenForDisplay(zipName)));
    await addBase64Attachment(item, base64, zipName);
    for (const attachment of targets) {
      await removeAttachment(item, attachment.id);
    }

    const zippedBytes = base64Bytes(base64);
    const saved = originalBytes > 0 ? Math.round((1 - zippedBytes / originalBytes) * 100) : 0;
    await notifyInfo(
      KEY,
      t().zipResult(files.length, shortenForDisplay(zipName), formatBytes(zippedBytes), saved) +
        t().skippedNotice(inlineCount, cloudCount)
    );
  } catch (error) {
    await notifyError(KEY, t().zipFailed((error as Error).message));
  } finally {
    event.completed();
  }
}

/** Converts the payload Outlook handed us into something JSZip can store. */
function toZipInput(name: string, content: Office.AttachmentContent): ZipInput | undefined {
  const safeName = sanitizeFileName(name, "attachment");
  switch (content.format) {
    case Office.MailboxEnums.AttachmentContentFormat.Base64:
      return { name: safeName, base64: content.content };
    case Office.MailboxEnums.AttachmentContentFormat.Eml:
      // Attached messages come back as an .eml payload, base64 on some hosts
      // and raw text on others.
      return payloadEntry(ensureExtension(safeName, ".eml"), content.content);
    case Office.MailboxEnums.AttachmentContentFormat.ICalendar:
      return payloadEntry(ensureExtension(safeName, ".ics"), content.content);
    default:
      // "url" - a cloud attachment, which has no bytes to compress.
      return undefined;
  }
}

function payloadEntry(name: string, content: string): ZipInput {
  return looksLikeBase64(content) ? { name, base64: content } : { name, text: content };
}

function looksLikeBase64(value: string): boolean {
  return value.length > 64 && /^[A-Za-z0-9+/\s]+={0,2}\s*$/.test(value);
}

/** Names the archive after the subject, falling back to the localized default. */
async function buildZipName(item: Office.MessageCompose, existingNames: string[]): Promise<string> {
  const fallback = t().defaultArchiveName;
  // Dutch clients prefix replies with "Antw:" and forwards with "Doorst:".
  const subject = (await getSubject(item))
    .replace(/^((re|fw|fwd|antw|doorst)\s*:\s*)+/i, "")
    .trim();
  const stem = sanitizeFileName(subject, fallback).slice(0, 60).trim() || fallback;
  const taken = new Set(existingNames.map((n) => n.toLowerCase()));
  let name = `${stem}.zip`;
  for (let i = 2; taken.has(name.toLowerCase()); i++) {
    name = `${stem} (${i}).zip`;
  }
  return name;
}
